import {
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { buildBannerPlaceholderUrl } from '../../utils/bannerImage';

const rows = [
  {
    platform: 'PurelyGive',
    platformFee: '0%',
    subscription: '$0',
    processing: 'Donor supported',
    net: '$100',
    bestFor: 'Simple donation pages',
    href: '/pricing',
    summary: 'Best if you want simple setup and maximum net donations.',
  },
  {
    platform: 'Classy',
    platformFee: '1–5%',
    subscription: '~$299+',
    processing: '2.2–2.4% + $0.30',
    net: '~$94.50',
    bestFor: 'Enterprise fundraising',
    href: '/compare/classy',
    summary: 'Best if you run complex enterprise campaigns.',
  },
  {
    platform: 'Donorbox',
    platformFee: '2.95%',
    subscription: '$0',
    processing: 'Processor fees',
    net: '~$94',
    bestFor: 'Small nonprofits',
    href: '/compare/donorbox',
    summary: 'Best if you want a familiar SMB-focused option.',
  },
  {
    platform: 'Givebutter',
    platformFee: '0–3%',
    subscription: '$0',
    processing: 'Varies',
    net: '~$96.70',
    bestFor: 'Campaigns + events',
    href: '/compare/givebutter',
    summary: 'Best if events and campaigns are central to fundraising.',
  },
  {
    platform: 'Stripe',
    platformFee: '0%',
    subscription: '$0',
    processing: '~2.2% + $0.30',
    net: '~$97.50',
    bestFor: 'Custom dev builds',
    href: '/compare/stripe',
    summary: 'Best if you have in-house developers for a custom stack.',
  },
];

const hubTitle = 'Compare Donation Platforms | PurelyGive';
const hubDescription =
  'Compare PurelyGive with Classy, Donorbox, Givebutter, and Stripe to understand typical costs and net donation retained.';
const hubUrl = `https://${window.location.host}/compare`;
const hubImage = buildBannerPlaceholderUrl('Compare Donation Platforms');
const overviewVideoUrl = 'https://www.youtube.com/embed/Xfjr5WIs-mI?autoplay=1&rel=0';

const ComparisonHub = () => {
  const { isOpen: isVideoOpen, onOpen: onVideoOpen, onClose: onVideoClose } = useDisclosure();

  return (
  <Box bg="#f5f3ee" minH="100vh" py={14} px={{ base: 4, md: 6 }}>
    <Helmet>
      <title>{hubTitle}</title>
      <meta name="description" content={hubDescription} />
      <link rel="canonical" href={hubUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="PurelyGive" />
      <meta property="og:title" content={hubTitle} />
      <meta property="og:description" content={hubDescription} />
      <meta property="og:image" content={hubImage} />
      <meta property="og:url" content={hubUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={hubTitle} />
      <meta name="twitter:description" content={hubDescription} />
      <meta name="twitter:image" content={hubImage} />
    </Helmet>

    <Center>
      <Stack spacing={8} maxW="1100px" w="full">
        <Stack spacing={3}>
          <Heading as="h1" size="xl">Compare donation platforms</Heading>
          <Text color="gray.600">Compare fees, setup complexity, and net received from each $100 donation.</Text>
          <Text fontSize="sm" color="gray.500">Last updated: March 2026</Text>
        </Stack>

        <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 8 }} boxShadow="sm">
          <Box display={{ base: 'none', md: 'block' }} overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Platform</Th>
                  <Th>Platform Fee</Th>
                  <Th>Subscription</Th>
                  <Th>Processing</Th>
                  <Th>Net received from $100</Th>
                  <Th>Best For</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => (
                  <Tr key={row.platform}>
                    <Td>
                      <Text as={Link} to={row.href} color="orange.500" fontWeight="semibold">{row.platform}</Text>
                    </Td>
                    <Td>{row.platformFee}</Td>
                    <Td>{row.subscription}</Td>
                    <Td>{row.processing}</Td>
                    <Td fontWeight="semibold">{row.net}</Td>
                    <Td>{row.bestFor}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <Stack spacing={4} display={{ base: 'flex', md: 'none' }}>
            {rows.map((row) => (
              <Box key={row.platform} border="1px solid" borderColor="orange.100" borderRadius="xl" p={4}>
                <Text as={Link} to={row.href} color="orange.500" fontWeight="bold">{row.platform}</Text>
                <Text fontSize="sm">Platform fee: {row.platformFee}</Text>
                <Text fontSize="sm">Subscription: {row.subscription}</Text>
                <Text fontSize="sm">Processing: {row.processing}</Text>
                <Text fontSize="sm" fontWeight="semibold">Net from $100: {row.net}</Text>
                <Text fontSize="sm">Best for: {row.bestFor}</Text>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box bg="orange.500" color="white" borderRadius="2xl" p={{ base: 5, md: 7 }}>
          <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={4}>
            <Box>
              <Heading size="md" mb={2}>See PurelyGive in 2 minutes</Heading>
              <Text>Quick overview of how nonprofits keep 100% of each donation.</Text>
            </Box>
            <Button bg="white" color="orange.600" _hover={{ bg: 'orange.50' }} onClick={onVideoOpen}>
              Watch 2-minute overview
            </Button>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          {rows.filter((row) => row.href.startsWith('/compare/')).map((row) => (
            <Box key={row.platform} bg="white" borderRadius="xl" p={5} boxShadow="sm">
              <Heading size="sm" mb={2}>PurelyGive vs {row.platform}</Heading>
              <Text color="gray.600" mb={4}>{row.summary}</Text>
              <Button as={Link} to={row.href} colorScheme="orange" size="sm">View comparison</Button>
            </Box>
          ))}
        </SimpleGrid>
      </Stack>
    </Center>

    <Modal isOpen={isVideoOpen} onClose={onVideoClose} isCentered size={{ base: 'full', md: '4xl' }}>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="black" color="white" borderRadius={{ base: 0, md: 'xl' }}>
        <ModalBody p={{ base: 3, md: 4 }}>
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="semibold">PurelyGive 2-minute overview</Text>
            <CloseButton color="white" onClick={onVideoClose} />
          </Flex>
          <Box position="relative" pb="56.25%" h={0} borderRadius="md" overflow="hidden">
            <iframe
              title="PurelyGive two-minute overview"
              src={isVideoOpen ? overviewVideoUrl : 'about:blank'}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </Box>
          <Button
            as="a"
            href="https://www.youtube.com/watch?v=Xfjr5WIs-mI"
            target="_blank"
            rel="noreferrer"
            leftIcon={<ExternalLinkIcon />}
            mt={3}
            size="sm"
            variant="ghost"
            color="white"
          >
            Open on YouTube
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  </Box>
  );
};

export default ComparisonHub;
